import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import TableCell from '@mui/material/TableCell';

import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import useNotification from '../../../../components/_hook/useNotification';
import DialogConfirm from '../../../../components/_dialog/DialogConfirm';
import { useUnpublishShippingMethodsMutation } from '../../../../../graphql/mutation/shippingMethod/unpublishShippingMethodsMutation';
import StatusIconButton from '../../../../components/_iconButton/StatusIconButton';
import { useDeleteShippingMethodsMutation } from '../../../../../graphql/mutation/shippingMethod/deleteShippingMethodsMutation';
import CopyIconButton from '../../../../components/_iconButton/CopyIconButton';
import { usePublishShippingMethodsMutation } from '../../../../../graphql/mutation/shippingMethod/publishShippingMethodsMutation';
import DeleteIconButton from '../../../../components/_iconButton/DeleteIconButton';
import useLanguage from '../../../../components/_hook/useLanguage';
import Moment from '../../../../components/Moment';
import useRoutes from '../../../../components/_hook/useRoutes';
import Context from '../../../../context';
import { IShippingMethodSchema } from '../../../../../graphql/type/shippingMethod';

interface IProps {
  item: IShippingMethodSchema;
  setTableRefreshKey?: (tableRefreshKey: number) => void;
}

export default function BusinessShippingMethodTableItem(props: IProps) {
  const { t } = useTranslation();
  const { item, setTableRefreshKey } = props;
  const { notification } = useNotification();
  const { path } = useRoutes();
  const { company } = useContext(Context);
  const { getText } = useLanguage();
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const [
    deleteShippingMethodsMutation,
    { loading: isDeletingShippingMethods },
  ] = useDeleteShippingMethodsMutation('isSuccess', {
    onCompleted: () => {
      notification.success(
        t('__item___deleted', { item: `${item._id} ${t('shipping_method')}` })
      );
      if (setTableRefreshKey) {
        setTableRefreshKey(new Date().getTime());
      }
    },
    onError: (e) => {
      notification.error(e.message);
    },
  });
  const [
    publishShippingMethodsMutation,
    { loading: isPublishingShippingMethods },
  ] = usePublishShippingMethodsMutation(
    `updatedShippingMethods {
				_id
				status
			}`,
    {
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const [
    unpublishShippingMethodsMutation,
    { loading: isUnpublishingShippingMethods },
  ] = useUnpublishShippingMethodsMutation(
    `updatedShippingMethods {
				_id
				status
			}`,
    {
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  async function onClickPublish() {
    if (item._id) {
      await publishShippingMethodsMutation({ variables: { _ids: [item._id] } });
    }
  }

  async function onClickUnpublish() {
    if (item._id) {
      await unpublishShippingMethodsMutation({
        variables: { _ids: [item._id] },
      });
    }
  }

  async function onClickDelete() {
    if (item._id) {
      await deleteShippingMethodsMutation({ variables: { _ids: [item._id] } });
    }
  }

  return (
    <>
      <DialogConfirm
        title={t('are_you_sure_you_want_to_delete')}
        open={isOpenDeleteDialog}
        onClose={() => setIsOpenDeleteDialog(false)}
        onConfirm={onClickDelete}
      />
      <TableCell align="left">
        <Tooltip title={item._id}>
          <Button
            color="primary"
            component={Link}
            to={path('business.shipping_method.detail', {
              companyID: company._id,
              docID: item._id,
            })}
          >
            {item._id.substr(0, 6)}
          </Button>
        </Tooltip>
        <CopyIconButton iconHtmlFontSize="0.8rem" copyText={item._id} />
      </TableCell>
      <TableCell align="center">
        {item.name && Boolean(item.name.length) && item.name[0].text
          ? getText(item.name)
          : ''}
      </TableCell>
      <TableCell align="center">
        {item.created && item.created.time && (
          <Moment time={item.created.time} />
        )}
      </TableCell>
      <TableCell align="center">
        {item.updated && item.updated.time && (
          <Moment time={item.updated.time} />
        )}
      </TableCell>
      <TableCell align="right">
        <StatusIconButton
          status={item.status}
          loading={isUnpublishingShippingMethods || isPublishingShippingMethods}
          tooltip={item.status ? t('publish') : t('unpublish')}
          onClick={item.status ? onClickUnpublish : onClickPublish}
        />
        <DeleteIconButton
          tooltip={t('delete')}
          loading={isDeletingShippingMethods}
          onClick={() => setIsOpenDeleteDialog(true)}
        />
      </TableCell>
    </>
  );
}
